import { computed, onBeforeUnmount, onMounted, ref, shallowRef } from 'vue'
import { getGlobalStat, toNum, type GlobalStat } from '@/api/global'
import { onSessionCleared } from '@/stores/auth'
import { useWebSocket, type WsMessage } from './useWebSocket'

/** One sample in the sliding window. Speeds are bytes/s. */
export interface SpeedPoint {
  /** epoch ms */
  ts: number
  down: number
  up: number
}

export interface UseSpeedHistoryOptions {
  /** how many samples to retain (1 sample/s ⇒ 60 = 60s window) */
  windowSize?: number
  /** sampling period in ms */
  intervalMs?: number
  /** start sampling on mount */
  immediate?: boolean
  /** subscribe to `/api/ws/tasks` for push-driven refresh */
  useWs?: boolean
}

/** `tellActive` entry — only the fields the window needs. */
interface ActiveTaskLike {
  downloadSpeed?: string | number
  uploadSpeed?: string | number
}

/**
 * 60-second sliding speed window for the dashboard chart.
 *
 * Data strategy (matches sa4-task.md "方案 B" as closely as the backend allows):
 *
 *   - `/api/ws/tasks` is the *push* source. The server (`tasks_webui.go:
 *     pushLoop`) emits `{"type":"active","tasks":[...]}` every 3s; there is no
 *     `global:stat` event, so the aggregate speed is recomputed by summing the
 *     per-task `downloadSpeed`/`uploadSpeed`. Each push refreshes the window
 *     immediately, giving sub-poll latency when tasks are running.
 *   - A 1s timer keeps the window advancing between pushes (the chart must scroll
 *     even while idle) and reuses the last pushed value instead of issuing a
 *     request. A lightweight `/api/global/stat` GET is only used to seed the
 *     first sample and to recover whenever the socket is not open — so a healthy
 *     WS connection costs 0 RPS, not 1 RPS/client.
 *
 * The window is always kept exactly `windowSize` long (pre-filled with zeros) so
 * the chart's x-axis does not stretch as data arrives.
 */
export function useSpeedHistory(options: UseSpeedHistoryOptions = {}) {
  const {
    windowSize = 60,
    intervalMs = 1000,
    immediate = true,
    useWs = true,
  } = options

  const history = ref<SpeedPoint[]>([])
  const current = shallowRef<GlobalStat | null>(null)
  const error = ref<string | null>(null)
  const paused = ref(false)

  /** latest speeds seen from any source, reused by the ticker between pushes */
  let latestDown = 0
  let latestUp = 0
  /** ms timestamp of the last WS-sourced sample; drives the polling fallback */
  let lastPushAt = 0

  let timer: number | null = null
  let unsubscribe: (() => void) | null = null
  let disposed = false

  function seedWindow(): void {
    const now = Date.now()
    const seed: SpeedPoint[] = []
    for (let i = windowSize - 1; i >= 0; i--) {
      seed.push({ ts: now - i * intervalMs, down: 0, up: 0 })
    }
    history.value = seed
  }

  function pushPoint(down: number, up: number, ts = Date.now()): void {
    const next = history.value.slice()
    next.push({ ts, down, up })
    while (next.length > windowSize) next.shift()
    history.value = next
  }

  /** Fold an active-task array into aggregate speeds. */
  function sumActive(tasks: unknown): { down: number; up: number } | null {
    if (!Array.isArray(tasks)) return null
    let down = 0
    let up = 0
    for (const t of tasks as ActiveTaskLike[]) {
      down += toNum(t?.downloadSpeed)
      up += toNum(t?.uploadSpeed)
    }
    return { down, up }
  }

  async function fetchStat(): Promise<void> {
    try {
      const stat = await getGlobalStat()
      if (disposed) return
      current.value = stat
      latestDown = stat.downloadSpeed
      latestUp = stat.uploadSpeed
      error.value = null
    } catch (e) {
      if (disposed) return
      error.value = (e as Error).message ?? 'failed to load global stat'
    }
  }

  // ---- WebSocket push -----------------------------------------------------

  const ws = useWs
    ? useWebSocket({
        url: () => {
          const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
          return `${proto}//${window.location.host}/api/ws/tasks`
        },
        autoReconnect: true,
        stopOnSessionClear: true,
        onOpen: () => {
          // server defaults to ["active"]; ask explicitly for completion events too
          ws?.send({ action: 'subscribe', events: ['active', 'completed', 'error'] })
        },
      })
    : null

  const wsConnected = computed(() => ws?.status.value === 'open')

  function handleWsMessage(msg: WsMessage): void {
    if (paused.value || disposed) return
    if (msg.type !== 'active') return
    const tasks = (msg as { tasks?: unknown }).tasks
    const sums = sumActive(tasks)
    if (!sums) return
    latestDown = sums.down
    latestUp = sums.up
    lastPushAt = Date.now()
    // reflect push-derived speeds in the stat cards without an extra request
    if (current.value) {
      current.value = {
        ...current.value,
        downloadSpeed: sums.down,
        uploadSpeed: sums.up,
        numActive: Array.isArray(tasks) ? tasks.length : current.value.numActive,
      }
    }
    pushPoint(sums.down, sums.up)
  }

  // ---- sampling loop ------------------------------------------------------

  async function tick(): Promise<void> {
    if (paused.value || disposed) return
    // No fresh push within 2 push intervals ⇒ socket is idle or down: poll once.
    const stale = Date.now() - lastPushAt > 6000
    if (!wsConnected.value || stale) {
      await fetchStat()
    }
    if (disposed) return
    pushPoint(latestDown, latestUp)
  }

  function start(): void {
    if (timer !== null) return
    timer = window.setInterval(() => void tick(), intervalMs)
  }

  function stop(): void {
    if (timer !== null) {
      window.clearInterval(timer)
      timer = null
    }
  }

  function pause(): void {
    paused.value = true
  }

  function resume(): void {
    paused.value = false
  }

  function toggle(): void {
    paused.value = !paused.value
  }

  function clear(): void {
    seedWindow()
  }

  onMounted(() => {
    seedWindow()
    if (!immediate) return
    void fetchStat()
    if (ws) {
      unsubscribe = ws.subscribe(handleWsMessage)
      ws.connect()
    }
    start()
  })

  onBeforeUnmount(() => {
    disposed = true
    stop()
    unsubscribe?.()
    ws?.disconnect()
  })

  // v3.14.0 (UI 打磨): 会话清除 (登出/401) → 停轮询 + 断 WS + 置 disposed。
  // 此前登出后 1s fetch 循环继续打未授权请求, WS 无限重连; 重新登录由
  // 路由重挂载 (App 层 :key) 自然重建实例。
  onSessionCleared(() => {
    disposed = true
    stop()
    ws?.disconnect()
  })

  const maxDown = computed(() =>
    history.value.reduce((m, p) => (p.down > m ? p.down : m), 0),
  )
  const maxUp = computed(() =>
    history.value.reduce((m, p) => (p.up > m ? p.up : m), 0),
  )

  return {
    /** sliding window, always `windowSize` samples long */
    history,
    /** latest global stat (cards) */
    current,
    error,
    paused,
    wsConnected,
    wsStatus: computed(() => ws?.status.value ?? 'idle'),
    maxDown,
    maxUp,
    start,
    stop,
    pause,
    resume,
    toggle,
    clear,
    refresh: fetchStat,
  }
}
